import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  signUp,
  signIn,
  fetchCurrentUser,
  type FieldErrors,
  type SignInPayload,
  type SignInResponse,
  type SignUpPayload,
  type SignUpResponse,
} from "./authAPI";
import { clearTokens, saveTokens, getUserCookie } from "./tokenStorage";

interface AuthUser {
  id: number;
  name: string;
  email: string;
  is_active?: boolean;
  pwd_reset_required?: boolean;
  groups?: string[];
  permissions?: unknown[];
  company_member_id?: number;
  [key: string]: unknown;
}

interface AuthState {
  isAuthenticated: boolean;
  username: string | null;
  user: AuthUser | null;
  userGroups: string[] | null;
  accessToken: string | null;
  refreshToken: string | null;
  profilePictureId: number | null;
  companyProfilePictureId: number | null;
  /** Cached company profile image URL for fast display (e.g. in job preview modal) */
  companyProfilePictureUrl: string | null;
  signupStatus: "idle" | "loading" | "succeeded" | "failed";
  signupError: string | null;
  signupFieldErrors: FieldErrors | null;
  signupMessage: string | null;
  loginStatus: "idle" | "loading" | "succeeded" | "failed";
  loginError: string | null;
  loginFieldErrors: FieldErrors | null;
}

const initialState: AuthState = {
  isAuthenticated: false,
  username: null,
  user: null,
  userGroups: null,
  accessToken: null,
  refreshToken: null,
  profilePictureId: null,
  companyProfilePictureId: null,
  companyProfilePictureUrl: null,
  signupStatus: "idle",
  signupError: null,
  signupFieldErrors: null,
  signupMessage: null,
  loginStatus: "idle",
  loginError: null,
  loginFieldErrors: null,
};

export const signUpUser = createAsyncThunk<
  SignUpResponse,
  SignUpPayload,
  { rejectValue: { message: string; fieldErrors?: FieldErrors } }
>("auth/signUpUser", async (payload, { rejectWithValue }) => {
  try {
    return await signUp(payload);
  } catch (error) {
    if (error instanceof Error) {
      const fieldErrors = (error as Error & { fieldErrors?: FieldErrors })
        .fieldErrors;
      return rejectWithValue({
        message: error.message,
        fieldErrors,
      });
    }
    return rejectWithValue({
      message: "Unable to complete sign up. Please try again.",
    });
  }
});

export const signInUser = createAsyncThunk<
  SignInResponse,
  SignInPayload,
  { rejectValue: { message: string; fieldErrors?: FieldErrors } }
>("auth/signInUser", async (payload, { rejectWithValue }) => {
  try {
    const loginResponse = await signIn(payload);

    const accessToken = loginResponse.data?.token?.access;

    if (accessToken) {
      try {
        const currentUserResponse = await fetchCurrentUser(accessToken);
        const currentUser = currentUserResponse.data;

        if (currentUser) {
          loginResponse.data = loginResponse.data ?? {};
          const existingUser = loginResponse.data.user ?? {};

          loginResponse.data.user = {
            ...existingUser,
            ...currentUser,
          };
        }
      } catch (error) {
        console.error("Failed to fetch current user profile:", error);
      }
    }

    return loginResponse;
  } catch (error) {
    if (error instanceof Error) {
      const fieldErrors = (error as Error & { fieldErrors?: FieldErrors })
        .fieldErrors;
      return rejectWithValue({
        message: error.message,
        fieldErrors,
      });
    }
    return rejectWithValue({
      message: "Unable to sign in. Please try again.",
    });
  }
});

export const fetchAndRestoreUserData = createAsyncThunk<
  AuthUser,
  { forceRefresh?: boolean } | void,
  { rejectValue: { message: string } }
>("auth/fetchAndRestoreUserData", async (payload, { rejectWithValue, getState }) => {
  try {
    const cachedUser = getUserCookie();
    const forceRefresh = Boolean(payload && payload.forceRefresh);
    // Only trust cookie without refetch when groups are present (needed for route rules).
    if (
      !forceRefresh &&
      cachedUser &&
      typeof cachedUser === "object" &&
      "id" in cachedUser &&
      Array.isArray((cachedUser as AuthUser).groups)
    ) {
      return cachedUser as AuthUser;
    }
    const state = getState() as { auth: AuthState };
    const accessToken = state.auth.accessToken;

    if (!accessToken) {
      return rejectWithValue({
        message: "No access token available.",
      });
    }

    const currentUserResponse = await fetchCurrentUser(accessToken);
    const currentUser = currentUserResponse.data;

    if (!currentUser) {
      return rejectWithValue({
        message: "Failed to fetch user data.",
      });
    }
    const userToSave: AuthUser = {
      id: currentUser.id,
      name: currentUser.name,
      email: currentUser.email,
      is_active: currentUser.is_active,
      pwd_reset_required: currentUser.pwd_reset_required,
      groups: currentUser.groups,
      permissions: currentUser.permissions,
      company_member_id: currentUser.company_member_id,
    };

    const refreshToken = state.auth.refreshToken;
    if (refreshToken) {
      saveTokens(accessToken, refreshToken, false, userToSave);
    }

    return userToSave;
  } catch (error) {
    if (error instanceof Error) {
      return rejectWithValue({
        message: error.message,
      });
    }
    return rejectWithValue({
      message: "Unable to fetch user data. Please try again.",
    });
  }
});

export const ensureCurrentUser = createAsyncThunk<
  AuthUser,
  void,
  { state: { auth: AuthState }; rejectValue: { message: string } }
>("auth/ensureCurrentUser", async (_, { getState, rejectWithValue }) => {
  try {
    const state = getState();
    const existingUser = state.auth.user;
    const existingGroups = state.auth.userGroups;
    if (existingUser?.id && existingGroups !== null) {
      return existingUser;
    }

    const accessToken = state.auth.accessToken;
    if (!accessToken) {
      return rejectWithValue({ message: "No access token available." });
    }

    const currentUserResponse = await fetchCurrentUser(accessToken);
    const currentUser = currentUserResponse.data;

    if (!currentUser) {
      return rejectWithValue({ message: "Failed to fetch user data." });
    }

    const userToSave: AuthUser = {
      id: currentUser.id,
      name: currentUser.name,
      email: currentUser.email,
      is_active: currentUser.is_active,
      pwd_reset_required: currentUser.pwd_reset_required,
      groups: currentUser.groups,
      permissions: currentUser.permissions,
      company_member_id: currentUser.company_member_id,
    };

    return userToSave;
  } catch (error) {
    if (error instanceof Error) {
      return rejectWithValue({ message: error.message });
    }
    return rejectWithValue({
      message: "Unable to fetch user data. Please try again.",
    });
  }
});

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    login: (state, action: PayloadAction<string>) => {
      state.isAuthenticated = true;
      state.username = action.payload;
    },
    logout: (state) => {
      state.isAuthenticated = false;
      state.username = null;
      state.user = null;
      state.userGroups = null;
      state.accessToken = null;
      state.refreshToken = null;
      state.profilePictureId = null;
      state.companyProfilePictureId = null;
      state.companyProfilePictureUrl = null;
      clearTokens();
    },
    updateProfilePicture: (state, action: PayloadAction<number | null>) => {
      state.profilePictureId = action.payload;
    },
    updateCompanyProfilePicture: (state, action: PayloadAction<number | null>) => {
      state.companyProfilePictureId = action.payload;
    },
    updateCompanyProfilePictureUrl: (state, action: PayloadAction<string | null>) => {
      state.companyProfilePictureUrl = action.payload;
    },
    restoreSession: (
      state,
      action: PayloadAction<{ accessToken: string; refreshToken: string }>,
    ) => {
      state.isAuthenticated = true;
      // restore user from cookie cache
      const cachedUser = getUserCookie();
      if (cachedUser && typeof cachedUser === "object" && "id" in cachedUser) {
        state.user = cachedUser as AuthUser;
        state.username = cachedUser.name ?? cachedUser.email ?? null;
        state.userGroups = Array.isArray((cachedUser as AuthUser).groups)
          ? ((cachedUser as AuthUser).groups as string[])
          : null;
      }
      state.accessToken = action.payload.accessToken;
      state.refreshToken = action.payload.refreshToken;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signUpUser.pending, (state) => {
        state.signupStatus = "loading";
        state.signupError = null;
        state.signupFieldErrors = null;
        state.signupMessage = null;
      })
      .addCase(signUpUser.fulfilled, (state, action) => {
        state.signupStatus = "succeeded";
        state.signupMessage =
          action.payload?.message ?? "Account created successfully.";
        if (action.payload?.username || action.payload?.name) {
          state.username =
            action.payload.username ?? action.payload.name ?? null;
        }
        state.signupFieldErrors = null;
      })
      .addCase(signUpUser.rejected, (state, action) => {
        state.signupStatus = "failed";
        state.signupError =
          action.payload?.message ??
          action.error.message ??
          "Unable to complete sign up. Please try again.";
        state.signupFieldErrors = action.payload?.fieldErrors ?? null;
      })
      .addCase(signInUser.pending, (state) => {
        state.loginStatus = "loading";
        state.loginError = null;
        state.loginFieldErrors = null;
      })
      .addCase(signInUser.fulfilled, (state, action) => {
        state.loginStatus = "succeeded";
        state.loginError = null;
        state.loginFieldErrors = null;

        const user = action.payload.data?.user ?? null;
        const token = action.payload.data?.token;
        // Do not use [] when groups are missing — that is reserved for true super-admins (API returns []).
        const groups =
          user && Array.isArray(user.groups)
            ? (user.groups as string[])
            : null;

        state.user = user;
        state.userGroups = groups;
        state.username = user?.name ?? user?.email ?? null;
        state.isAuthenticated = true;
        state.accessToken = token?.access ?? null;
        state.refreshToken = token?.refresh ?? null;

        if (token?.access && token?.refresh && user) {
          saveTokens(
            token.access,
            token.refresh,
            action.meta.arg.rememberMe ?? false,
            user
          );
        }
      })
      .addCase(signInUser.rejected, (state, action) => {
        state.loginStatus = "failed";
        state.loginError =
          action.payload?.message ??
          action.error.message ??
          "Unable to sign in. Please try again.";
        state.loginFieldErrors = action.payload?.fieldErrors ?? null;
        state.isAuthenticated = false;
        state.user = null;
        state.userGroups = null;
        state.accessToken = null;
        state.refreshToken = null;
      })
      .addCase(fetchAndRestoreUserData.fulfilled, (state, action) => {
        state.user = action.payload;
        state.userGroups = Array.isArray(action.payload.groups)
          ? (action.payload.groups as string[])
          : null;
        state.username = action.payload.name ?? action.payload.email ?? null;
      })
      .addCase(ensureCurrentUser.fulfilled, (state, action) => {
        state.user = action.payload;
        state.userGroups = Array.isArray(action.payload.groups)
          ? (action.payload.groups as string[])
          : null;
        state.username = action.payload.name ?? action.payload.email ?? null;
      })
      .addCase(fetchAndRestoreUserData.rejected, (state, action) => {
        console.error("Failed to restore user data:", action.payload?.message);
      })
      .addCase(ensureCurrentUser.rejected, (state, action) => {
        console.error("Failed to ensure current user data:", action.payload?.message);
      });
  },
});

export const { login, logout, restoreSession, updateProfilePicture, updateCompanyProfilePicture, updateCompanyProfilePictureUrl } = authSlice.actions;
export default authSlice.reducer;
